import React, { useEffect, useState, type FC, type MouseEventHandler } from 'react';

import { faDollar } from '@fortawesome/free-solid-svg-icons/faDollar';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import axios, { type AxiosResponse } from 'axios';

import { Button, Panel, Table, NavLinkButton, Row, Col, Text } from '@shared/components/bootstrap';
import { Currency } from '@shared/components/helpers';
import { useNumericURLSearchParam } from '@shared/hooks/use_numeric_url_search_param';
import { useURLSearchParam } from '@shared/hooks/use_url_search_param';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { InputFormGroup } from '@admin/components/fields/input_form_group';
import { SelectFormGroup } from '@admin/components/fields/select_form_group';
import { Permission } from '@admin/components/helpers/permission';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';
import { MARKETING } from '@admin/libraries/access_control_catalog.generated';
import type { IPagination } from '@admin/types/pagination';

type Referral = {
  id: number;
  amount: number;
  coupon?: {
    name: string;
  };
  state: string;
  referrer: {
    id: number;
    name: string;
  };
  referrer_type: 'Account' | 'User' | 'Ambassador' | 'Affiliate';
  referable: {
    id: number;
    name: string;
  };
  created_at: string;
};

type ReferralRequestResponse = {
  pagination: IPagination;
  results: Referral[];
};

type ReferralsRequestParams = {
  page?: number;
  state?: string;
  type?: string;
  from?: string;
  till?: string;
};

const useReferralPayRequest = ({ id }: { id: number }) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<void>> | undefined>(undefined);

  const execute = () => {
    const request = axios.put(`/marketing/referrals/${id}.json`, {
      state: 'paid',
    });
    setRequest(request);
    return request;
  };

  return [execute, useAxiosRequest(request)] as const;
};

const useReferralsRequest = ({ state, type, from, till, page }: ReferralsRequestParams) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<ReferralRequestResponse>> | undefined>(undefined);

  useEffect(() => {
    const request = axios.get('/marketing/referrals.json', {
      params: {
        state,
        type,
        from,
        till,
        page,
      },
    });
    setRequest(request);
  }, [state, type, from, till, page]);

  return useAxiosRequest(request);
};

const MIN_DATE = DateTime.now().minus({ years: 2 });
const MAX_DATE = DateTime.now();

const DEFAULT_PAGE = 1;

const ReferralPayButton: FC<{
  referral: Referral;
  onPay(): void;
}> = ({ referral, onPay }) => {
  const [execute, { loading }] = useReferralPayRequest({ id: referral.id });

  const { confirm, confirmation } = useConfirmation();

  if (referral.state !== 'payment_due') return null;
  if (referral.referrer_type !== 'User') return null;

  const onClick: MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading) return;

    const confirmed = await confirm({
      title: 'Pay Referral',
      description: `Did you pay ${referral.referrer.name}?`,
    });

    if (confirmed !== ConfirmationResponse.Confirm) return;

    await execute();

    onPay();
  };

  return (
    <>
      <Button kind="primary" onClick={onClick}>
        <FontAwesomeIcon icon={faDollar} /> Pay
      </Button>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}
    </>
  );
};

const ReferralReferrerLink: FC<{ referral: Referral }> = ({ referral }) => {
  switch (referral.referrer_type) {
    case 'Account':
      return <NavLinkButton to={`/accounts/${referral.referrer.id}`}>{referral.referrer.name}</NavLinkButton>;
    case 'User':
      return <NavLinkButton to={`/users/${referral.referrer.id}`}>{referral.referrer.name}</NavLinkButton>;
    case 'Ambassador':
      return <NavLinkButton to={`/affiliates/${referral.referrer.id}/edit`}>{referral.referrer.name}</NavLinkButton>;
    case 'Affiliate':
      return <NavLinkButton to={`/affiliates/${referral.referrer.id}/edit`}>{referral.referrer.name}</NavLinkButton>;
    default:
      return <Text>{referral.referrer.name}</Text>;
  }
};

const ReferralReferrerType: FC<{ referral: Referral }> = ({ referral }) => {
  switch (referral.referrer_type) {
    case 'Account':
      return <span>Customer</span>;
    case 'User':
      return <span>Employee</span>;
    case 'Ambassador':
      return <span>Ambassador</span>;
    case 'Affiliate':
      return <span>Affiliate</span>;
    default:
      return <span>-</span>;
  }
};

export const ReferralsList: FC = () => {
  useTitle('Referrals');
  const [page, setPage] = useNumericURLSearchParam('page', DEFAULT_PAGE);
  const [state, setState] = useURLSearchParam<string>('state');
  const [type, setType] = useURLSearchParam<string>('type');
  const [from, setFrom] = useURLSearchParam<string>('from');
  const [till, setTill] = useURLSearchParam<string>('till');
  const { loading, response, refetch } = useReferralsRequest({
    page,
    state,
    type,
    from,
    till,
  });

  const pagination = response?.data?.pagination;
  const results = response?.data?.results;

  return (
    <Panel>
      <Panel.Body>
        <div className="mb-4">
          <Row>
            <Col md={3}>
              <SelectFormGroup
                id="filters[type]"
                label="Type:"
                value={type ?? ''}
                onChange={(event) => setType(event.target.value || undefined)}
              >
                <option value="">All</option>
                <option value="User">Employee</option>
                <option value="Account">Customer</option>
                <option value="Ambassador">Ambassador</option>
                <option value="Affiliate">Affiliate</option>
              </SelectFormGroup>
            </Col>
            <Col md={3}>
              <SelectFormGroup
                id="filters[state]"
                label="State:"
                value={state ?? ''}
                onChange={(event) => setState(event.target.value || undefined)}
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="payment_due">Due</option>
                <option value="paid">Paid</option>
                <option value="no_payment_due">No Payment Due</option>
              </SelectFormGroup>
            </Col>
            <Col md={2}>
              <InputFormGroup
                id="filters[from]"
                required
                type="date"
                label="From:"
                min={MIN_DATE.toISODate()}
                max={MAX_DATE.toISODate()}
                value={from ?? ''}
                onChange={(event) => setFrom(event.target.value || undefined)}
              />
            </Col>
            <Col md={2}>
              <InputFormGroup
                id="filters[till]"
                required
                type="date"
                label="Till:"
                min={MIN_DATE.toISODate()}
                max={MAX_DATE.toISODate()}
                value={till ?? ''}
                onChange={(event) => setTill(event.target.value || undefined)}
              />
            </Col>
            <Permission resource={MARKETING.resource} action={MARKETING.actions.update} denied={null}>
              <Col md={2}>
                <Text alignment="right" tag="div">
                  <NavLinkButton kind="mint" to="/marketing/referrals/new">
                    <FontAwesomeIcon icon={faPlus} /> Add
                  </NavLinkButton>
                </Text>
              </Col>
            </Permission>
          </Row>
        </div>

        <Table responsive striped>
          <thead>
            <tr>
              <th className="col-sm-0">ID</th>
              <th className="col-sm-2">Referrer</th>
              <th className="col-sm-2">Referable</th>
              <th className="col-sm-1">Type</th>
              <th className="col-sm-1">Amount</th>
              <th className="col-sm-2">Coupon</th>
              <th className="col-sm-2">State</th>
              <th className="col-sm-2">Date</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {results?.map((referral) => (
              <tr key={referral.id}>
                <td className="col-sm-0">{referral.id}</td>
                <td className="col-sm-2">
                  <ReferralReferrerLink referral={referral} />
                </td>
                <td className="col-sm-2">
                  <NavLinkButton to={`/accounts/${referral.referable.id}`}>{referral.referable.name}</NavLinkButton>
                </td>
                <td className="col-sm-1">
                  <ReferralReferrerType referral={referral} />
                </td>
                <td className="col-sm-1">
                  <Currency value={0 - referral.amount} />
                </td>
                <td className="col-sm-2">{referral.coupon?.name ?? '-'}</td>
                <td className="col-sm-2">{referral.state}</td>
                <td className="col-sm-2">
                  {DateTime.fromISO(referral.created_at).toLocaleString(DateTime.DATE_SHORT)}
                </td>
                <td>
                  <Permission resource={MARKETING.resource} action={MARKETING.actions.update} denied={null}>
                    <ReferralPayButton
                      referral={referral}
                      onPay={() => {
                        refetch();
                      }}
                    />
                  </Permission>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {loading && <Spinner />}
        {pagination && <Pagination pagination={pagination} onPage={setPage} />}
      </Panel.Body>
      <Permission resource={MARKETING.resource} action={MARKETING.actions.update} denied={null}>
        <Panel.Footer align="right">
          <NavLinkButton kind="mint" to="/marketing/referrals/new">
            <FontAwesomeIcon icon={faPlus} /> Add
          </NavLinkButton>
        </Panel.Footer>
      </Permission>
    </Panel>
  );
};
